import {StrictMode} from "react"
import {AppStoreProvider} from "@/providers/AppStoreProvider"
import {createRoot} from "react-dom/client"
import {AppRouterProvider} from "@/providers/AppRouterProvider"
import {AppI18nProvider} from "@/providers/AppI18nProvider"
import "@/assets/drop.scss"
import "@reown/appkit-wallet-button/react"
import {createAppKit} from "@reown/appkit/react"
import {
  solana,
  solanaDevnet,
} from "@reown/appkit/networks"
import {QueryClient, QueryClientProvider} from "@tanstack/react-query"
import {SolanaAdapter} from "@reown/appkit-adapter-solana"
import {
  DefaultSIWX,
  SolanaVerifier,
} from "@reown/appkit-siwx"
import {NotifyingLocalStorage} from "@/utils/NotifyingLocalStorage"
import {ToastProvider} from "@/components/ui/Toast/Toast"
import type {AppKitNetwork} from "@reown/appkit-common"
import {BalanceProvider} from "@/context/BalanceContext.tsx"
import {ProgramProvider} from "@/context/ProgramContext.tsx"
import {UserPoolsProvider} from "@/context/UserPoolsContext.tsx"
import {createTheme, ThemeProvider} from '@mui/material/styles'
import * as Sentry from "@sentry/react";

Sentry.init({
  dsn: "https://c794dbae0c75805f8cf4f48ea7d0368c@o4509764407066624.ingest.de.sentry.io/4509764408705104",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  debug: true,
  integrations: [
    Sentry.replayIntegration()
  ],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const root =
  document.getElementById("root") ??
  (() => {
    const r = document.createElement("div")
    document.appendChild(r)
    return r
  })()

const metadata = {
  //optional
  name: "ZNX HUB",
  description:
    "Zenex ecosystem with ZNX Play, ZNX Arena and ZNX Invest for diversified investment opportunities with guaranteed returns and exciting gameplay.",
  url: "https://zenexhub.com",
  icons: ["https://zenexhub.com/assets/460x460znx.png"], //TODO: Иконка Zenex hub
}

const projectId = import.meta.env.VITE_PROJECT_ID

const queryClient = new QueryClient()

const [networks, defaultNetwork] = ((env): [[AppKitNetwork, ...AppKitNetwork[]], AppKitNetwork] => {
  if (env?.VITE_DEVNET_ENABLE && env.VITE_DEVNET_ENABLE == "yes") {
    return [[solanaDevnet], solanaDevnet]
  }
  return [[solana], solana]
})(import.meta.env)

export const solanaAdapter = new SolanaAdapter({
  connectionSettings: {
    commitment: "confirmed",
  },
})

console.debug('default networks', defaultNetwork)
console.debug('default usdc mint', import.meta.env.VITE_USDC_MINT)
console.debug('default znx mint', import.meta.env.VITE_ZNX_MINT)

createAppKit({
  adapters: [solanaAdapter /*, wagmiAdapter*/],
  projectId,
  networks: networks,
  defaultNetwork: defaultNetwork,
  metadata,
  themeMode: "dark",
  features: {
    analytics: true,
    socials: ["google", "discord", "x"],
    email: true,
  },
  siwx: new DefaultSIWX({
    verifiers: [
      // new EIP155Verifier(),
      // new BIP122Verifier(),
      new SolanaVerifier(),
    ],
    storage: new NotifyingLocalStorage({key: "siwx-sessions"}),
  }),
})

const theme = createTheme({
  typography: {
    fontFamily: 'Montserrat, Urbanist, sans-serif',
  },
}) /*mui components global font*/

createRoot(root).render(
  <StrictMode>
    <ThemeProvider theme={theme}>
      <AppI18nProvider>
        <AppStoreProvider>
          {/*<WagmiProvider config={wagmiAdapter.wagmiConfig}>*/}
          <QueryClientProvider client={queryClient}>
            <ToastProvider>
              <ProgramProvider>
                <BalanceProvider>
                  <UserPoolsProvider>

                    <AppRouterProvider/>
                  </UserPoolsProvider>
                </BalanceProvider>
              </ProgramProvider>
            </ToastProvider>
          </QueryClientProvider>
          {/*</WagmiProvider>*/}
        </AppStoreProvider>
      </AppI18nProvider>
    </ThemeProvider>
  </StrictMode>,
)
